import type {
  Alert,
  AlertEvidence,
  AnalyticsSite,
  AnalyticsSummary,
  SiteAnalyticsDetail,
  ApiResult,
  Competitor,
  DashboardStats,
  NotificationSettings,
  WatchedUrl,
  WatchedUrlSnapshot,
  Workspace,
  WorkspaceMember,
  WorkspaceInvitation,
} from "@/types";

const BASE_URL = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ?? "/api";

// ─── Auth token storage ───────────────────────────────────────────────────────

let _token: string | null = null;

export function setAuthToken(token: string | null) {
  _token = token;
  if (token) {
    localStorage.setItem("ms_token", token);
  } else {
    localStorage.removeItem("ms_token");
  }
}

export function getAuthToken(): string | null {
  if (_token) return _token;
  _token = localStorage.getItem("ms_token");
  return _token;
}

// ─── Active workspace persistence ────────────────────────────────────────────

export function setActiveWorkspaceId(id: string | null) {
  if (id) localStorage.setItem("ms_active_workspace", id);
  else localStorage.removeItem("ms_active_workspace");
}

export function getActiveWorkspaceId(): string | null {
  return localStorage.getItem("ms_active_workspace");
}

// ─── Credits ──────────────────────────────────────────────────────────────────

/** Fired after anything that may have spent credits, so credit meters refresh. */
export const CREDITS_CHANGED = "credits:changed";
const CREDIT_SPENDING = /^\/(chat\/usage\/increment|kyc\/(discover|mira|analyze-competitor))/;

export interface Credits {
  plan: "trial" | "starter" | "growth";
  allowance: number;
  used: number;
  remaining: number;
  period: string;
  /** When the monthly allowance refills */
  resetsAt: string;
  costs?: { chat: number; mira: number; discovery: number; analysis: number };
}

// ─── Upgrade prompts ──────────────────────────────────────────────────────────

/** Fired when a request hits a plan limit or runs out of credits; UpgradeDialog listens. */
export const UPGRADE_NEEDED = "upgrade:needed";

export interface UpgradeNeededDetail {
  /** Why, in the user's terms — usually the server's error message */
  message: string;
  /** Machine-readable reason, e.g. "OUT_OF_CREDITS" or "DOMAIN_LIMIT_REACHED" */
  reason?: string;
}

/** Open the upgrade dialog from anywhere. */
export function requestUpgrade(detail: UpgradeNeededDetail) {
  window.dispatchEvent(new CustomEvent<UpgradeNeededDetail>(UPGRADE_NEEDED, { detail }));
}

/** Display name for a plan key — the free plan is stored as "trial". */
export const planName = (plan: string | null | undefined) =>
  plan === "growth" ? "Growth" : plan === "starter" ? "Starter" : "Free";

// ─── Base fetch ───────────────────────────────────────────────────────────────

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };
  const token = getAuthToken();
  if (token && !headers.Authorization) headers.Authorization = `Bearer ${token}`;

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      ...options,
      headers,
      credentials: "include", // send HttpOnly cookies automatically
    });
    const responseText = await res.text();
    let json: Record<string, unknown> = {};
    try {
      json = responseText ? JSON.parse(responseText) as Record<string, unknown> : {};
    } catch {
      // Not JSON: usually an HTML error page from Cloudflare or a proxy. Never show the markup.
      const looksLikeHtml = /^\s*</.test(responseText);
      json = {
        message: !looksLikeHtml && responseText
          ? responseText.slice(0, 300)
          : res.status >= 500
            ? "Peersheep couldn't reach the server. Please try again in a moment."
            : "Request failed",
      };
    }
    if (!res.ok) {
      const message = typeof json.message === "string" ? json.message : "Request failed";
      const reason = typeof json.code === "string" ? json.code : undefined;
      // 402 = a plan limit or out of credits. (Billing's own 409s, e.g. a
      // downgrade that doesn't fit, are not upgrade moments.)
      if (res.status === 402 || (res.status === 403 && reason === "WORKSPACE_LIMIT_REACHED")) {
        requestUpgrade({ message, reason });
      }
      return { error: { message, code: String(res.status), reason } };
    }
    if (options.method === "POST" && CREDIT_SPENDING.test(path)) window.dispatchEvent(new Event(CREDITS_CHANGED));
    return { data: json as unknown as T };
  } catch (err) {
    return { error: { message: err instanceof Error ? err.message : "Network error" } };
  }
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const auth = {
  signup: (email: string, password: string, name: string, workspaceName: string) =>
    request<{ token: string; user: { id: string; email: string; name: string; avatarUrl: string | null }; workspace: Workspace }>(
      "/auth/signup",
      { method: "POST", body: JSON.stringify({ email, password, name, workspaceName }) }
    ),

  login: (email: string, password: string) =>
    request<{ token: string; user: { id: string; email: string; name: string; avatarUrl: string | null }; workspace: Workspace }>(
      "/auth/login",
      { method: "POST", body: JSON.stringify({ email, password }) }
    ),

  logout: () => request<{ success: boolean }>("/auth/logout", { method: "POST" }),

  me: () =>
    request<{ user: { id: string; email: string; name: string; avatarUrl: string | null }; workspace: Workspace }>("/auth/me"),

  updateProfile: (patch: { name?: string; avatarUrl?: string | null }) =>
    request<{ id: string; email: string; name: string; avatarUrl: string | null }>(
      "/auth/me", { method: "PATCH", body: JSON.stringify(patch) }
    ),
};

// ─── Dashboard ────────────────────────────────────────────────────────────────

export const dashboard = {
  stats: () => request<DashboardStats>("/dashboard/stats"),
};

// ─── Know your competitors (Mira discovery) ───────────────────────────────────

// ─── Know your competitors ────────────────────────────────────────────────────

export interface KycCompetitor {
  id: string;
  name: string;
  domain: string;
  description: string;
  similarity: "high" | "medium" | "low";
  suggestedPages: { path: string; label: string }[];
  /** Why Mira judged it a competitor, from its own homepage ("Added by you" for manual ones) */
  reason: string | null;
  source: "discovered" | "manual";
  summary: string | null;
  pricingModel: string | null;
  analyzedAt: string | null;
  /** What the user picked to monitor; null until they change it (defaults apply) */
  monitor: { selected: boolean; pages: string[] } | null;
}

export interface KycDiscovery {
  discoveryId: string;
  productName: string;
  productCategory: string;
  productDescription: string;
  productUrl: string;
  workspaceDomain: string | null;
  pagesScraped: number;
  discoveredAt: string;
  refreshedAt: string | null;
  fromCache?: boolean;
  competitors: KycCompetitor[];
}

export interface KycRank {
  /** OpenPageRank authority, 0–10 */
  oprScore: number | null;
  globalRank: number | null;
  tier: "high" | "medium" | "low" | "unknown";
  /** Authority-weighted count of sites linking to the domain */
  linkingDomains: number | null;
  /** Pages of the site in the Common Crawl index (approx.) — footprint, not backlinks */
  indexedPages: number;
}

export interface KycSpeed {
  mobileScore: number | null;
  desktopScore: number | null;
  mobileA11y: number | null;
  desktopA11y: number | null;
  lcp: number | null;
  cls: number | null;
  inp: number | null;
  fcp: number | null;
  ttfb: number | null;
}

export interface KycSeo {
  organicKeywords: number | null;
  organicTraffic: number | null;
  trafficValue: number | null;
  topKeywords: Array<{ keyword: string; position: number; volume: number; url: string }>;
}

export interface MiraMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  unverified: string[];
  createdAt: string;
}

export interface MiraReply {
  answer: string;
  unverified: string[];
  suggestions: string[];
  generatedAt: string;
  cached: boolean;
}

export const kyc = {
  /** The saved discovery, or null before Mira has run */
  discovery: () => request<KycDiscovery | null>("/kyc/discover"),
  discover: (productDescription?: string, force = false) =>
    request<KycDiscovery>("/kyc/discover", { method: "POST", body: JSON.stringify({ productDescription, force }) }),
  /** Keyed by domain; includes the workspace's own domain */
  ranks: () => request<Record<string, KycRank>>("/kyc/domain-rank?includeSelf=true"),
  speeds: () => request<Record<string, KycSpeed>>("/kyc/page-speed?includeSelf=true"),
  seo: () => request<Record<string, KycSeo>>("/kyc/seo?includeSelf=true"),
  refresh: () => request<{ queued: boolean }>("/kyc/refresh", { method: "POST" }),
  addCompetitor: (domain: string, name?: string) =>
    request<KycCompetitor>("/kyc/competitors", { method: "POST", body: JSON.stringify({ domain, name }) }),
  /** Removes it and stops Mira suggesting it again */
  removeCompetitor: (id: string) => request<{ removed: boolean }>(`/kyc/competitors/${id}`, { method: "DELETE" }),
  addToWatchlist: (competitors: { name: string; domain: string; pages: { path: string; label: string }[] }[]) =>
    request<{ message: string; totalUrls: number }>("/kyc/add-to-watchlist", { method: "POST", body: JSON.stringify({ competitors }) }),
  /** No question → Mira's briefing on the page; otherwise an answer grounded in the same data (and saved) */
  mira: (question?: string) =>
    request<MiraReply>("/kyc/mira", { method: "POST", body: JSON.stringify({ question }) }),
  /** The user's saved conversation with Mira on this page */
  miraHistory: () => request<{ messages: MiraMessage[] }>("/kyc/mira/history"),
  clearMira: () => request<{ cleared: boolean }>("/kyc/mira/history", { method: "DELETE" }),
  /** Remember whether a competitor is monitored and which of its pages */
  saveMonitor: (domain: string, selected: boolean, pages: string[]) =>
    request<{ saved: boolean }>("/kyc/monitor-prefs", { method: "PUT", body: JSON.stringify({ domain, selected, pages }) }),
};

// ─── Onboarding ───────────────────────────────────────────────────────────────

export interface OnboardingStatus {
  /** Setup finished (or pages already watched): /onboarding is closed */
  completed: boolean;
  domain: string | null;
  /** The user's company email domain, to pre-fill the website field */
  suggestedDomain: string | null;
  hasDiscovery: boolean;
  competitors: number;
  watchedUrls: number;
  alertChannels: { email: boolean; slack: boolean };
  alerts: number;
  chats: number;
  plan: "trial" | "starter" | "growth";
  /** Account-wide; null = unlimited */
  limits: { domains: number | null; urls: number | null };
  used: { domains: number; urls: number };
}

export const onboardingApi = {
  status: () => request<OnboardingStatus>("/onboarding/status"),
  /** Set the product website during setup (no DNS verification needed) */
  setDomain: (domain: string) =>
    request<{ domain: string; changed: boolean }>("/onboarding/domain", { method: "POST", body: JSON.stringify({ domain }) }),
  /** Sends the "you're live" email, once per workspace */
  complete: () => request<{ sent: boolean }>("/onboarding/complete", { method: "POST" }),
};

// ─── Competitors ──────────────────────────────────────────────────────────────

export const competitors = {
  list: () => request<Competitor[]>("/competitors"),
  create: (domain: string, name: string) =>
    request<Competitor>("/competitors", {
      method: "POST",
      body: JSON.stringify({ domain, name }),
    }),
  delete: (id: string) =>
    request<{ success: boolean }>(`/competitors/${id}`, { method: "DELETE" }),
  suggestUrls: (domain: string) =>
    request<{ url: string; label: string }[]>(`/competitors/suggest-urls`, {
      method: "POST",
      body: JSON.stringify({ domain }),
    }),
  timeline: (id: string) =>
    request<Array<{
      id: string; signal_score: string; page_type: string; summary: string;
      change_excerpt: string | null; detected_at: string; status: string;
      feedback: string | null; url: string; label: string;
    }>>(`/competitors/${id}/timeline`),
};

// ─── Watched URLs ─────────────────────────────────────────────────────────────

export const watchedUrls = {
  snapshot: (id: string) => request<WatchedUrlSnapshot>(`/watched-urls/${id}/snapshot`),
  list: (competitorId?: string) =>
    request<WatchedUrl[]>(competitorId ? `/watched-urls?competitorId=${competitorId}` : "/watched-urls"),
  create: (payload: {
    competitorId: string;
    url: string;
    label: string;
    crawlFrequency: "hourly" | "daily";
  }) =>
    request<WatchedUrl>("/watched-urls", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  update: (id: string, payload: Partial<Pick<WatchedUrl, "label" | "crawlFrequency" | "isActive">>) =>
    request<WatchedUrl>(`/watched-urls/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  delete: (id: string) =>
    request<{ success: boolean }>(`/watched-urls/${id}`, { method: "DELETE" }),
  crawlNow: (id: string) =>
    request<{ queued: boolean }>(`/watched-urls/${id}/crawl`, { method: "POST" }),
};

// ─── Alerts ───────────────────────────────────────────────────────────────────

export const alerts = {
  list: (params?: {
    competitorId?: string;
    signalScore?: string;
    pageType?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) => {
    const qs = params
      ? "?" + new URLSearchParams(Object.entries(params).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)])).toString()
      : "";
    return request<{ alerts: Alert[]; total: number; page: number; limit: number }>(`/alerts${qs}`);
  },
  get: (id: string) => request<Alert>(`/alerts/${id}`),
  evidence: (id: string) => request<AlertEvidence>(`/alerts/${id}/evidence`),
  markRead: (id: string) =>
    request<Alert>(`/alerts/${id}/read`, { method: "POST" }),
  markAllRead: () =>
    request<{ updated: number }>("/alerts/read-all", { method: "POST" }),
  archive: (id: string) =>
    request<Alert>(`/alerts/${id}/archive`, { method: "POST" }),
  feedback: (id: string, feedback: "useful" | "not_useful") =>
    request<{ success: boolean }>(`/alerts/${id}/feedback`, {
      method: "POST",
      body: JSON.stringify({ feedback }),
    }),
};

// ─── Notification settings ────────────────────────────────────────────────────

export const notifications = {
  get: () => request<NotificationSettings>("/notifications/settings"),
  update: (payload: Partial<NotificationSettings>) =>
    request<NotificationSettings>("/notifications/settings", {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  testSlack: () =>
    request<{ sent: boolean }>("/notifications/test-slack", { method: "POST" }),
  testEmail: () =>
    request<{ sent: boolean }>("/notifications/test-email", { method: "POST" }),
};

// ─── Analytics sites ───────────────────────────────────────────────────────────

export const analytics = {
  list: () => request<AnalyticsSite[]>("/analytics/sites"),
  summary: () => request<AnalyticsSummary>("/analytics/summary"),
  siteSummary: (siteId: string) => request<SiteAnalyticsDetail>(`/analytics/sites/${encodeURIComponent(siteId)}/summary`),
  create: (payload: { name: string; domain: string }) =>
    request<AnalyticsSite>("/analytics/sites", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  snippet: (siteKey: string) =>
    request<{ snippet: string }>(`/analytics/snippet?siteKey=${encodeURIComponent(siteKey)}`),
  track: (payload: Record<string, unknown>) =>
    request<{ ok: boolean; siteId: string; event: string }>("/analytics/track", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
};

// ─── Chat ─────────────────────────────────────────────────────────────────────

export const chat = {
  sessions: () => request<{
    id: string; domain: string; title: string;
    message_count: number; created_at: string; updated_at: string;
  }[]>("/chat/sessions"),

  sessionMessages: (id: string) =>
    request<{
      session: { id: string; domain: string; title: string };
      messages: { id: string; role: string; content: string; created_at: string }[];
    }>(`/chat/sessions/${id}/messages`),

  deleteSession: (id: string) =>
    request<{ success: boolean }>(`/chat/sessions/${id}`, { method: "DELETE" }),

  /** What Mira knows about a competitor; `refresh` rebuilds it instead of using the cache */
  context: (domain: string, refresh = false) =>
    request<{
      domain: string; builtAt: string; pagesScraped: number; totalChars: number;
      pages: { url: string; label: string; source: "monitored" | "live"; capturedAt: string; chars: number }[];
      skipped: { url: string; reason: string }[];
      context: string;
    }>("/chat/context", { method: "POST", body: JSON.stringify({ domain, refresh }) }),

  usage: () =>
    request<{
      used: number;
      limit: number | null;
      remaining: number | null;
      plan: string;
      period: string;
      unlimited: boolean;
    }>("/chat/usage"),

  incrementUsage: () =>
    request<{
      used: number;
      limit: number | null;
      remaining: number | null;
      plan: string;
      period: string;
      unlimited: boolean;
    }>("/chat/usage/increment", { method: "POST" }),
};

// ─── Scrape history ───────────────────────────────────────────────────────────

export const scrapeHistory = {
  list: (limit = 50) =>
    request<{
      id: string; url: string; page_type: string; status: number;
      used_browser: number; crawl_ms: number; content_length: number;
      analysis_score: string | null; analysis_summary: string | null; scraped_at: string;
    }[]>(`/scrape/history?limit=${limit}`),

  screenshots: () =>
    request<{
      id: string;
      url: string;
      fullPage: boolean;
      capturedAt: string;
      breakpoints: Record<string, {
        screenshot: string;
        width: number;
        height: number;
        label: string;
      }>;
    }[]>("/scrape/screenshots"),
};

// ─── CMO reports ──────────────────────────────────────────────────────────────

export const cmoReports = {
  list: () =>
    request<{
      id: string; domain: string; competitors: string[];
      seo_score: number; seo_grade: string; pages_scraped: number; analyzed_at: string;
    }[]>("/cmo/reports"),

  get: (id: string) => request<Record<string, unknown>>(`/cmo/reports/${id}`),
};

// ─── Workspace ────────────────────────────────────────────────────────────────

export const workspaceApi = {
  get: () => request<{
    id: string; name: string; slug: string; ownerId: string;
    plan: string; trialEndsAt: string | null;
    domain: string | null; logoUrl: string | null; createdAt: string;
  }>("/workspace"),

  list: () => request<Array<{
    id: string; name: string; slug: string;
    domain: string | null; logoUrl: string | null; plan: string;
  }>>("/workspace/list"),

  switch: (workspaceId: string) => request<{
    token: string;
    workspace: { id: string; name: string; slug: string; domain: string | null; logoUrl: string | null; plan: string; trialEndsAt: string | null };
  }>("/workspace/switch", { method: "POST", body: JSON.stringify({ workspaceId }) }),

  create: (name: string, domain?: string | null) => request<{
    id: string; name: string; slug: string; domain: string | null;
  }>("/workspace/create", { method: "POST", body: JSON.stringify({ name, domain }) }),

  update: (patch: { name?: string; domain?: string | null; logoUrl?: string | null }) =>
    request<{ id: string; name: string; domain: string | null; logoUrl: string | null }>(
      "/workspace", { method: "PATCH", body: JSON.stringify(patch) }
    ),

  delete: () => request<{
    success: boolean;
    loggedOut?: boolean;
    token?: string;
    workspace?: { id: string; name: string; slug: string; domain: string | null; logoUrl: string | null; plan: string; trialEndsAt: string | null };
  }>('/workspace', { method: 'DELETE' }),
};

// ─── Team ─────────────────────────────────────────────────────────────────────

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scope: "read" | "write";
  lastUsedAt: string | null;
  createdAt: string;
  createdBy: string;
}

// ─── Integrations (Slack app, Linear, webhooks) ───────────────────────────────

export type Signal = "high" | "medium" | "low";
export type IntegrationProvider = "slack" | "linear";

interface IntegrationBase {
  available: boolean;
  connected: boolean;
  status: "active" | "error" | null;
  lastError: string | null;
  externalName: string | null;
  minSignal: Signal;
  connectedAt: string | null;
}
export interface SlackIntegration extends IntegrationBase {
  provider: "slack";
  channel: string | null;
  configurationUrl: string | null;
}
export interface LinearTeam { id: string; name: string; key: string }
export interface LinearIntegration extends IntegrationBase {
  provider: "linear";
  team: LinearTeam | null;
}
export interface Webhook {
  id: string;
  url: string;
  minSignal: Signal;
  enabled: boolean;
  lastStatus: number | null;
  lastDeliveredAt: string | null;
  createdAt: string;
}
export interface Integrations { slack: SlackIntegration; linear: LinearIntegration; webhooks: Webhook[] }

export const integrationsApi = {
  list: () => request<Integrations>("/integrations"),
  /** Returns the provider's consent URL; send the browser there */
  connect: (provider: IntegrationProvider) =>
    request<{ url: string }>(`/integrations/${provider}/connect`, {
      method: "POST",
      body: JSON.stringify({ origin: window.location.origin }),
    }),
  complete: (provider: IntegrationProvider, code: string, state: string) =>
    request<SlackIntegration | LinearIntegration>(`/integrations/${provider}/complete`, {
      method: "POST",
      body: JSON.stringify({ code, state }),
    }),
  update: (provider: IntegrationProvider, patch: { minSignal?: Signal; teamId?: string }) =>
    request<SlackIntegration | LinearIntegration>(`/integrations/${provider}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  disconnect: (provider: IntegrationProvider) =>
    request<{ disconnected: boolean }>(`/integrations/${provider}`, { method: "DELETE" }),
  testSlack: () => request<{ sent: boolean }>("/integrations/slack/test", { method: "POST" }),
  linearTeams: () => request<LinearTeam[]>("/integrations/linear/teams"),
  linearIssueFor: (alertId: string) =>
    request<{ identifier: string; url: string } | null>(`/integrations/linear/issues/${alertId}`),
  createLinearIssue: (alertId: string) =>
    request<{ identifier: string; url: string }>("/integrations/linear/issues", {
      method: "POST",
      body: JSON.stringify({ alertId }),
    }),

  /** The signing secret is returned here and can be revealed again later */
  createWebhook: (url: string, minSignal: Signal) =>
    request<{ webhook: Webhook; secret: string }>("/integrations/webhooks", {
      method: "POST",
      body: JSON.stringify({ url, minSignal }),
    }),
  updateWebhook: (id: string, patch: { enabled?: boolean; minSignal?: Signal }) =>
    request<Webhook>(`/integrations/webhooks/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteWebhook: (id: string) =>
    request<{ deleted: boolean }>(`/integrations/webhooks/${id}`, { method: "DELETE" }),
  webhookSecret: (id: string) => request<{ secret: string }>(`/integrations/webhooks/${id}/secret`),
  testWebhook: (id: string) =>
    request<{ status: number; delivered: boolean }>(`/integrations/webhooks/${id}/test`, { method: "POST" }),
};

export const apiKeysApi = {
  list: () => request<ApiKey[]>("/api-keys"),

  /** The plaintext key is only in this response — it can't be fetched again */
  create: (name: string, scope: "read" | "write") =>
    request<Omit<ApiKey, "lastUsedAt" | "createdBy"> & { key: string }>("/api-keys", {
      method: "POST",
      body: JSON.stringify({ name, scope }),
    }),

  revoke: (id: string) =>
    request<{ revoked: boolean }>(`/api-keys/${id}`, { method: "DELETE" }),
};

export const teamApi = {
  /** List all current workspace members */
  members: () => request<WorkspaceMember[]>("/team/members"),

  /** List pending (and recently accepted/expired) invitations */
  invitations: () => request<WorkspaceInvitation[]>("/team/invitations"),

  /** Send an email invitation */
  invite: (email: string, role: "member" = "member") =>
    request<WorkspaceInvitation>("/team/invitations", {
      method: "POST",
      body: JSON.stringify({ email, role }),
    }),

  /** Revoke a pending invitation */
  revokeInvitation: (invitationId: string) =>
    request<{ success: boolean }>(`/team/invitations/${invitationId}`, { method: "DELETE" }),

  /** Remove a member from the workspace */
  removeMember: (memberId: string) =>
    request<{ success: boolean }>(`/team/members/${memberId}`, { method: "DELETE" }),

  /** Change a member's role */
  updateMemberRole: (memberId: string, role: "member" | "owner") =>
    request<WorkspaceMember>(`/team/members/${memberId}`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    }),

  /** Look up an invitation by token (public — no auth required) */
  getInvitation: (token: string) =>
    request<{ email: string; role: string; workspaceName: string }>(
      `/team/invitations/${token}`
    ),

  /** Accept an invitation — creates account and returns a JWT */
  acceptInvitation: (token: string, name: string, password: string) =>
    request<{
      token: string;
      user: { id: string; email: string; name: string };
      workspace: { id: string; name: string };
    }>(`/team/invitations/${token}/accept`, {
      method: "POST",
      body: JSON.stringify({ name, password }),
    }),

  /** Accept an invitation when already logged in — no signup needed */
  acceptInvitationLoggedIn: (token: string) =>
    request<{
      token: string;
      user: { id: string; email: string; name: string };
      workspace: { id: string; name: string };
    }>(`/team/invitations/${token}/accept-logged-in`, { method: "POST" }),
};

// ─── Billing ──────────────────────────────────────────────────────────────────

export interface BillingPlan {
  key: string;
  name: string;
  price: number;       // cents; Growth includes the owner's seat
  seatPrice: number | null; // cents per extra seat (Growth)
  interval: string;
  domains: number;     // across all the owner's workspaces
  urls: number;        // across all the owner's workspaces
  users: number;       // seats; -1 = unlimited
  workspaces: number;  // -1 = unlimited
  credits: number;     // monthly allowance
  productId: string;
}

/** Billing belongs to the user who owns the workspace; one plan covers all their workspaces. */
export interface BillingStatus {
  plan: string;
  subscriptionStatus: string;
  subscriptionId: string | null;
  productId: string | null;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  /** False for teammates — only the account owner can change the plan */
  isOwner: boolean;
  seats: { used: number; limit: number | null; billed: number | null; extraSeatPrice: number };
  credits: Credits | null;
  creditCosts: { chat: number; mira: number; discovery: number; analysis: number };
  workspaces: { used: number; limit: number | null };
  plans: BillingPlan[];
}

export const billingApi = {
  get: () => request<BillingStatus>("/billing"),
  /** This month's credit balance for the account */
  credits: () => request<Credits>("/billing/credits"),

  checkout: (plan: "starter" | "growth") =>
    request<{ url: string }>("/billing/checkout", {
      method: "POST",
      body: JSON.stringify({ plan }),
    }),

  portal: () =>
    request<{ url: string }>("/billing/portal", { method: "POST" }),

  cancel: () =>
    request<{ canceled: boolean }>("/billing/cancel", { method: "POST" }),
};

// ─── Email Intelligence ───────────────────────────────────────────────────────

export const emailIntelligence = {
  gmailAuthUrl: (redirectUri: string) =>
    request<{ url: string }>(`/email-intelligence/gmail-auth-url?redirectUri=${encodeURIComponent(redirectUri)}`),

  connections: () =>
    request<import("@/types").EmailConnection[]>("/email-intelligence/connections"),

  connect: (code: string, redirectUri: string) =>
    request<import("@/types").EmailConnection>("/email-intelligence/connections", {
      method: "POST",
      body: JSON.stringify({ code, redirectUri }),
    }),

  disconnect: (id: string) =>
    request<{ success: boolean }>(`/email-intelligence/connections/${id}`, { method: "DELETE" }),

  sync: (id: string) =>
    request<{ synced: number; skipped: number; hasMore: boolean }>(`/email-intelligence/connections/${id}/sync`, { method: "POST" }),

  startSync: (id: string) =>
    request<{ queued: boolean }>(`/email-intelligence/connections/${id}/sync/start`, { method: "POST" }),

  insights: (params?: {
    category?: string;
    signalScore?: string;
    competitorId?: string;
    domain?: string;
    q?: string;
    isRead?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  }) => {
    const qs = params
      ? "?" + new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v != null)
            .map(([k, v]) => [k, String(v)])
        ).toString()
      : "";
    return request<{ insights: import("@/types").EmailInsight[]; total: number; page: number; limit: number }>(
      `/email-intelligence/insights${qs}`
    );
  },

  insight: (id: string) =>
    request<import("@/types").EmailInsight>(`/email-intelligence/insights/${id}`),

  stats: () =>
    request<import("@/types").EmailStats>("/email-intelligence/stats"),
};

// ─── Plugins ──────────────────────────────────────────────────────────────────

export const pluginsApi = {
  /** List available plugins that can be authorized */
  list: () =>
    request<import("@/types").AvailablePlugin[]>("/plugins"),

  /** Get all active plugin authorizations for the workspace */
  authorizations: () =>
    request<import("@/types").PluginAuthorization[]>("/plugins/authorizations"),

  /** Grant a plugin authorization (exchange OAuth code for token) */
  authorize: (payload: import("@/types").PluginAuthorizationInput) =>
    request<import("@/types").PluginAuthorization>("/plugins/authorizations", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  /** Revoke a plugin authorization by ID */
  revoke: (id: string) =>
    request<{ success: boolean }>(`/plugins/authorizations/${id}`, { method: "DELETE" }),
};

// ─── Admin: social publishing ─────────────────────────────────────────────────

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const adminSocial = {
  config: () => request<import("@/types").SocialConfig>("/admin/social/config"),

  accounts: () => request<import("@/types").SocialAccount[]>("/admin/social/accounts"),

  connect: (platform: import("@/types").SocialConnectTarget, origin: string) =>
    request<{ url: string }>("/admin/social/accounts/connect", {
      method: "POST",
      body: JSON.stringify({ platform, origin }),
    }),

  completeConnect: (code: string, state: string) =>
    request<{ accounts: import("@/types").SocialAccount[] }>("/admin/social/accounts/complete", {
      method: "POST",
      body: JSON.stringify({ code, state }),
    }),

  connectBluesky: (handle: string, appPassword: string) =>
    request<{ accounts: import("@/types").SocialAccount[] }>("/admin/social/accounts/bluesky", {
      method: "POST",
      body: JSON.stringify({ handle, appPassword }),
    }),

  disconnect: (id: string) =>
    request<{ success: boolean }>(`/admin/social/accounts/${id}`, { method: "DELETE" }),

  posts: () => request<import("@/types").SocialPost[]>("/admin/social/posts"),

  createPost: (input: import("@/types").SocialPostInput) =>
    request<import("@/types").SocialPost>("/admin/social/posts", { method: "POST", body: JSON.stringify(input) }),

  bulkCreatePosts: (posts: import("@/types").SocialBulkPostInput[]) =>
    request<import("@/types").SocialBulkResult>("/admin/social/posts/bulk", {
      method: "POST",
      body: JSON.stringify({ posts }),
    }),

  updatePost: (id: string, input: import("@/types").SocialPostInput) =>
    request<import("@/types").SocialPost>(`/admin/social/posts/${id}`, { method: "PATCH", body: JSON.stringify(input) }),

  deletePost: (id: string) =>
    request<{ success: boolean }>(`/admin/social/posts/${id}`, { method: "DELETE" }),

  publishNow: (id: string) =>
    request<import("@/types").SocialPost>(`/admin/social/posts/${id}/publish`, { method: "POST" }),

  retry: (id: string) =>
    request<import("@/types").SocialPost>(`/admin/social/posts/${id}/retry`, { method: "POST" }),

  unschedule: (id: string) =>
    request<import("@/types").SocialPost>(`/admin/social/posts/${id}/unschedule`, { method: "POST" }),

  automationConfig: () =>
    request<import("@/types").SocialAutomationConfig>("/admin/social/automation-config"),

  updateAutomationConfig: (input: Partial<import("@/types").SocialAutomationConfig>) =>
    request<import("@/types").SocialAutomationConfig>("/admin/social/automation-config", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  automationPosts: (limit?: number) =>
    request<import("@/types").SocialPost[]>(
      `/admin/social/automation-posts${limit ? `?limit=${encodeURIComponent(limit)}` : ""}`,
    ),

  previewAutomationPost: (alertId: string) =>
    request<{ body: string; workspaceCompanyName: string }>("/admin/social/automation-preview", {
      method: "POST",
      body: JSON.stringify({ alertId }),
    }),

  draft: (topic: string, notes?: string) =>
    request<{ linkedin: string; x: string }>("/admin/social/draft", {
      method: "POST",
      body: JSON.stringify({ topic, notes }),
    }),

  /** Multipart upload; `request` always sends JSON, so this uses fetch directly. */
  /** Copy an image from a public https link into storage */
  imageFromUrl: (url: string) =>
    request<{ key: string; contentType: string; size: number }>("/admin/social/images/from-url", {
      method: "POST",
      body: JSON.stringify({ url }),
    }),

  uploadImage: async (
    file: File,
  ): Promise<{ data?: { key: string; contentType: string; size: number }; error?: { message: string } }> => {
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await fetch(`${BASE_URL}/admin/social/images`, {
        method: "POST",
        headers: authHeaders(),
        body: form,
        credentials: "include",
      });
      const json = (await res.json().catch(() => ({}))) as { key?: string; contentType?: string; message?: string };
      if (!res.ok || !json.key) return { error: { message: json.message ?? "Upload failed" } };
      return { data: { key: json.key, contentType: json.contentType ?? file.type, size: file.size } };
    } catch (err) {
      return { error: { message: err instanceof Error ? err.message : "Network error" } };
    }
  },

  /** Images are admin-only, so <img> can't load them directly; fetch with auth and return an object URL. */
  imageObjectUrl: async (key: string): Promise<string | null> => {
    try {
      const res = await fetch(`${BASE_URL}/admin/social/images/${key.replace(/^social\//, "")}`, {
        headers: authHeaders(),
        credentials: "include",
      });
      if (!res.ok) return null;
      return URL.createObjectURL(await res.blob());
    } catch {
      return null;
    }
  },
};
